/**
 * PremiumFooter — Apple/Linear-grade dark footer.
 * 4 columns + bottom legal strip. Strict palette (#111111 + #A855F7 + slate).
 */
import { Link } from "react-router-dom";
import { Twitter, Youtube, Linkedin, Instagram, Mail, Sparkles } from "lucide-react";

const FOOTER_NAV = [
  {
    title: "Product",
    links: [
      { label: "Dashboard", to: "/dashboard" },
      { label: "Workspace", to: "/workspace" },
      { label: "AI Studio", to: "/ai-studio" },
      { label: "Teaching Suite", to: "/teaching" },
      { label: "YouTube Suite", to: "/youtube-suite" },
      { label: "Content Suite", to: "/content-suite" },
      { label: "All Tools", to: "/tools" },
    ],
  },
  {
    title: "Popular tools",
    links: [
      { label: "AI SEO Toolkit", to: "/tools/seo/ai" },
      { label: "AI Keyword Research", to: "/free/keyword-research" },
      { label: "AI PPT Generator", to: "/tools/ai-slides" },
      { label: "AutoCaptions (Hinglish)", to: "/tools/auto-captions" },
      { label: "AI Thumbnail Studio", to: "/tools/design/thumbnail-studio" },
      { label: "PDF Editor Suite", to: "/tools/design/pdf" },
      { label: "Google Ads Copy Generator", to: "/tools/google-ads" },
      { label: "YouTube Channel Assessment", to: "/youtube-suite/channel-assessment" },
    ],
  },
  {
    title: "For teachers",
    links: [
      { label: "Lesson Planner", to: "/teaching/lesson-planner" },
      { label: "Worksheet Generator", to: "/teaching/worksheet" },
      { label: "Question Paper", to: "/teaching/question-paper" },
      { label: "Rubric Builder", to: "/teaching/rubric" },
      { label: "Parent Communication", to: "/teaching/parent-communication" },
      { label: "Course Outline", to: "/teaching/course-outline" },
      { label: "Homework Generator", to: "/teaching/homework" },
      { label: "Flashcards", to: "/teaching/flashcards" },
    ],
  },
  {
    title: "For creators",
    links: [
      { label: "Creator Earnings", to: "/creator/incentive" },
      { label: "YouTube CRM", to: "/studio" },
      { label: "Task Manager", to: "/studio/my-tasks" },
      { label: "SRT Captions", to: "/tools/srt-captions" },
      { label: "Social Media Suite", to: "/tools/social-media" },
      { label: "Design Tools", to: "/tools/design" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Pricing", to: "/pricing" },
      { label: "Blog", to: "/blogs" },
      { label: "Contact", to: "/contact" },
      { label: "For Agencies", to: "/agencies" },
      { label: "Reports", to: "/reports" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms of Service", to: "/terms" },
      { label: "Security", to: "/security" },
      { label: "Login", to: "/login" },
      { label: "Sign up", to: "/signup" },
    ],
  },
];

const SOCIALS = [
  { Icon: Twitter, href: "https://twitter.com/markeemix", label: "Twitter" },
  { Icon: Youtube, href: "https://youtube.com/@markeemix", label: "YouTube" },
  { Icon: Linkedin, href: "https://linkedin.com/company/markeemix", label: "LinkedIn" },
  { Icon: Instagram, href: "https://instagram.com/markeemix", label: "Instagram" },
];

export default function PremiumFooter() {
  return (
    <footer className="bg-[#111111] text-white font-sans relative overflow-hidden" data-testid="premium-footer">
      {/* Very subtle purple top edge */}
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] opacity-30"
           style={{ background: "radial-gradient(ellipse at top, rgba(168,85,247,0.25) 0%, rgba(168,85,247,0) 70%)" }}/>

      {/* CTA strip */}
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-12 border-b border-white/10">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
              Ready to ship faster?
            </h3>
            <p className="mt-2 text-white/60 max-w-md">
              Join early founding users, creators and marketers using MarkeeMix to plan, create, teach, and grow — all in one workspace.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/signup"
              data-testid="footer-cta-signup"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#A855F7] hover:bg-[#9333EA] text-white text-sm font-semibold transition-colors"
            >
              <Sparkles className="w-4 h-4"/> Start free
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/15 hover:border-white/30 text-sm font-semibold transition-colors"
            >
              Talk to sales
            </Link>
          </div>
        </div>
      </div>

      {/* Multi-col link grid */}
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-8">
        {/* Brand col */}
        <div className="col-span-2 md:col-span-4 lg:col-span-1">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="footer-logo">
            <span className="font-heading text-xl font-bold tracking-tight">
              Markee<span className="text-[#A855F7]">Mix</span>
            </span>
          </Link>
          <p className="mt-4 text-sm text-white/55 leading-relaxed">
            The AI Workspace for teachers, creators &amp; marketers. Apple-grade design, Linear-grade speed.
          </p>
          <div className="mt-5 flex items-center gap-2">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                data-testid={`footer-social-${s.label.toLowerCase()}`}
                className="w-8 h-8 rounded-lg border border-white/10 hover:border-[#A855F7] hover:bg-[#A855F7]/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
              >
                <s.Icon className="w-3.5 h-3.5"/>
              </a>
            ))}
          </div>
          <a
            href="mailto:hello@markeemix.com"
            className="mt-4 inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white transition-colors"
          >
            <Mail className="w-3 h-3"/> hello@markeemix.com
          </a>
        </div>

        {FOOTER_NAV.map((col) => (
          <div key={col.title}>
            <h4 className="font-heading text-xs uppercase tracking-[0.18em] font-bold text-white/40 mb-4">
              {col.title}
            </h4>
            <ul className="space-y-3">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/70 hover:text-[#A855F7] transition-colors"
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Bottom legal */}
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="text-xs text-white/45">
          © {new Date().getFullYear()} MarkeeMix Labs. All rights reserved.
        </div>
        <div className="flex items-center gap-5 text-xs text-white/45">
          <Link to="/privacy" className="hover:text-white/80 transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-white/80 transition-colors">Terms</Link>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
            All systems operational
          </span>
        </div>
      </div>
    </footer>
  );
}
